import { useEffect, useMemo } from 'react';

import { UseBaseTable } from 'hooks/useBaseTable';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useProductivityRateStore } from 'store/productivityRate.store';

import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { UseProductivityTableProvider } from 'components/widgets/RunRateActivitiesDrilldown/ProductivityTable.Provider';

interface Props {
    ui: UseProductivityTableProvider['ui'];
    api: UseProductivityTableProvider['api'];
    globalFilterData: IComponentProps['globalFilterData'];
    table: UseBaseTable['table'];
    latestVersionId: IComponentProps['latestVersionId'];
    title: string;
    subTitle: string;
}

export const useProductivityTableLogic = ({
    ui,
    globalFilterData,
    table,
    api,
    latestVersionId,
    title,
    subTitle,
}: Props) => {
    const { update } = useProductivityRateStore();
    const { handleSnackBar } = useCustomSnackBar();
    const { sendEventWithDimensions } = useEventWithDimensions();

    useEffect(() => {
        table.setPagination({ ...table.pagination, page: 1 });
    }, [
        ui.state.sorting,
        globalFilterData?.resourceType,
        globalFilterData?.quickAnswerFilter,
        globalFilterData?.resource,
        ui.vars.dateRangeFilterValue,
        ui.state.selectedSeries,
        ui.state?.activityCodes,
        ui.state?.milestoneFragnet,
        ui.state?.tags,
        ui.state?.wbs,
        ui.state?.activityType,
        ui.state?.productivityPreDefined,
    ]);
    useEffect(() => {
        update({
            selectedValue: null,
            selectedSeries: null,
            selectedStartDate: null,
            selectedEndDate: null,
            dataPointIndex: null,
            seriesIndex: null,
        });
    }, [globalFilterData]);

    useEffect(() => {
        if (api.getRunRateAnalysisBreakdown.data) {
            table.setTotalPages(api.getRunRateAnalysisBreakdown.data.numOfPages);
        }
    }, [api.getRunRateAnalysisBreakdown.data]);

    const filteredRows = useMemo(
        () => api.getRunRateAnalysisBreakdown.data?.cardList || [],
        [api.getRunRateAnalysisBreakdown.data],
    );

    const handleAPICsvExport = () => {
        if (latestVersionId) {
            api.extractProductivityTableCardToCsv.instance.mutate(
                {
                    ...api.extractProductivityTableCardToCsv.payload,
                    fileName: `${title}_${subTitle}`,
                },
                {
                    onSuccess: () => {
                        handleSnackBar('File Successfully Downloaded', 'success');

                        sendEventWithDimensions({
                            category: 'Export',
                            action: 'CSV',
                            label: `${title} ${subTitle}`,
                        });
                    },
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                },
            );
        }
    };

    return {
        logic: {
            filteredRows,
            handleAPICsvExport,
        },
    };
};
